import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit, Trash2, MoreHorizontal, Filter, FileText, Calendar, CheckCircle, XCircle, AlertTriangle, Clock, Eye, Mail, User, Send } from 'lucide-react';
import { getInviteList, createInviteList, updateInviteList, deleteInviteList, updateInviteListStatus } from '../utils/inviteListApi';
import { getAccessCodes } from '../utils/accessCodeApi';
import { apiCall } from '../utils/api';
import { SEND_MAIL, PUBLIC_SEND_MAIL_BY_INVITE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const InviteList = () => {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInvite, setEditingInvite] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);

  const [formData, setFormData] = useState({
    invite_id: '',
    firstname: '',
    email: '',
    access_code: '',
    create_at: ''
  });

  const [accessCodes, setAccessCodes] = useState([]);
  const [loadingAccessCodes, setLoadingAccessCodes] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // Ensure invites is always an array
  const safeInvites = Array.isArray(invites) ? invites : [];

  // Dynamic column configuration
  const getTableColumns = () => {
    if (safeInvites.length === 0) return [];
    
    const columns = [
      { key: 'firstname', label: 'First Name', type: 'text', width: 'w-32' },
      { key: 'email', label: 'Email', type: 'email', width: 'w-48' },
      { key: 'event_status', label: 'Status', type: 'status', width: 'w-24' },
      { key: 'opened', label: 'Opened', type: 'boolean', width: 'w-20' },
      { key: 'clicked', label: 'Clicked', type: 'boolean', width: 'w-20' },
      { key: 'create_at', label: 'Created At', type: 'date', width: 'w-32' },
      { key: 'actions', label: 'Actions', type: 'actions', width: 'w-32' }
    ];
    
    return columns;
  };

  const tableColumns = getTableColumns();

  // Render table cell based on column type
  const renderTableCell = (column, invite) => {
    const getFieldValue = (invite, possibleNames, defaultValue = null) => {
      for (const name of possibleNames) {
        if (invite[name] !== undefined && invite[name] !== null && invite[name] !== '') {
          return invite[name];
        }
      }
      return defaultValue;
    };

    switch (column.type) {
      case 'status':
        const statusValue = getFieldValue(invite, [column.key]);
        // Ensure status value is a string
        const statusString = statusValue !== null && statusValue !== undefined 
          ? (typeof statusValue === 'object' ? JSON.stringify(statusValue) : String(statusValue))
          : 'Unknown';
        const statusColors = {
          'pending': 'bg-yellow-100 text-yellow-800',
          'sent': 'bg-blue-100 text-blue-800',
          'opened': 'bg-green-100 text-green-800',
          'clicked': 'bg-purple-100 text-purple-800',
          'completed': 'bg-gray-100 text-gray-800',
          'failed': 'bg-red-100 text-red-800'
        };
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[statusString] || 'bg-gray-100 text-gray-800'}`}>
            {statusString}
          </span>
        );
      
      case 'boolean':
        const boolValue = getFieldValue(invite, [column.key]);
        return (
          <div className="flex items-center">
            {boolValue ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-gray-400" />
            )}
          </div>
        );
      
      case 'date':
        const dateValue = getFieldValue(invite, [column.key]);
        return (
          <span className="text-sm text-gray-500">
            {dateValue ? new Date(dateValue).toLocaleDateString() : 'N/A'}
          </span>
        );
      
      case 'email':
        const emailValue = getFieldValue(invite, [column.key]);
        // Ensure email value is a string
        const emailString = emailValue !== null && emailValue !== undefined 
          ? (typeof emailValue === 'object' ? JSON.stringify(emailValue) : String(emailValue))
          : 'No email';
        return (
          <div className="flex items-center space-x-2">
            <Mail className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-500">{emailString}</span>
          </div>
        );
      
      case 'actions':
        return (
          <div className="flex items-center space-x-2">
            {/**<button
              onClick={() => handleEdit(invite)}
              className="text-red-600 hover:text-red-900"
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </button>*/}
            <button
              onClick={() => handleResend(invite)}
              className="text-red-600 hover:text-red-900"
              title="Resend Email"
            >
              <Send className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDelete(invite.invite_id)}
              className="text-red-600 hover:text-red-900"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        );
      
      default:
        const textValue = getFieldValue(invite, [column.key]);
        // Ensure the value is converted to string to avoid React child object error
        const displayValue = textValue !== null && textValue !== undefined 
          ? (typeof textValue === 'object' ? JSON.stringify(textValue) : String(textValue))
          : `No ${column.label}`;
        return (
          <span className="text-sm text-gray-500">
            {displayValue}
          </span>
        );
    }
  };

  // Fetch invites from API
  const fetchInvites = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getInviteList();
      
      setApiResponse(response);
      
      if (response.success) {
        let inviteData = response.data;
        
        // Handle different API response structures
        if (response.data && response.data.data) {
          inviteData = response.data.data;
        } else if (response.data && response.data.invites) {
          inviteData = response.data.invites;
        } else if (response.data && response.data.items) {
          inviteData = response.data.items;
        } else if (response.data && response.data.results) {
          inviteData = response.data.results;
        } else if (response.data && response.data.list) {
          inviteData = response.data.list;
        } else if (response.data && response.data.records) {
          inviteData = response.data.records;
        }
        
        if (Array.isArray(inviteData)) {
          console.log('Setting invites from API:', inviteData);
          setInvites(inviteData);
        } else {
          console.warn('API returned data but it\'s not an array:', inviteData);
          setInvites([]);
        }
      } else {
        console.warn('API call failed:', response.error);
        setInvites([]);
      }
    } catch (error) {
      console.warn('API error:', error);
      // Fallback to mock data if API fails
      const mockData = [
        {
          invite_id: 'INV001',
          site_id: 'SITE001',
          firstname: 'John',
          email: 'john.doe@example.com',
          access_code: 'ABC123',
          event_id: 'EVT001',
          event_status: 'sent',
          event_action: 'survey_invite',
          opened: true,
          clicked: true,
          create_at: new Date().toISOString()
        },
        {
          invite_id: 'INV002',
          site_id: 'SITE001',
          firstname: 'Jane',
          email: 'jane.smith@example.com',
          access_code: 'DEF456',
          event_id: 'EVT001',
          event_status: 'pending',
          event_action: 'survey_invite',
          opened: false,
          clicked: false,
          create_at: new Date().toISOString()
        },
        {
          invite_id: 'INV003',
          site_id: 'SITE002',
          firstname: 'Mike',
          email: 'mike.wilson@example.com',
          access_code: 'GHI789',
          event_id: 'EVT002',
          event_status: 'opened',
          event_action: 'event_invite',
          opened: true,
          clicked: false,
          create_at: new Date().toISOString()
        }
      ];
      
      setApiResponse({ success: false, error: error.message });
      setInvites(mockData);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch access codes
  const fetchAccessCodes = useCallback(async () => {
    setLoadingAccessCodes(true);
    try {
      const response = await getAccessCodes();
      if (response.success && response.data) {
        let accessCodeData = response.data;
        
        // Handle different API response structures
        if (response.data && response.data.data) {
          accessCodeData = response.data.data;
        } else if (response.data && response.data.access_codes) {
          accessCodeData = response.data.access_codes;
        } else if (response.data && response.data.items) {
          accessCodeData = response.data.items;
        } else if (response.data && response.data.results) {
          accessCodeData = response.data.results;
        } else if (response.data && response.data.list) {
          accessCodeData = response.data.list;
        } else if (response.data && response.data.records) {
          accessCodeData = response.data.records;
        }
        
        if (Array.isArray(accessCodeData)) {
          setAccessCodes(accessCodeData);
        } else {
          setAccessCodes([]);
        }
      } else {
        setAccessCodes([]);
      }
    } catch (error) {
      console.warn('Failed to fetch access codes:', error);
      setAccessCodes([]);
    } finally {
      setLoadingAccessCodes(false);
    }
  }, []);

  // Fetch invites on component mount
  useEffect(() => {
    fetchInvites();
    fetchAccessCodes();
  }, [fetchInvites, fetchAccessCodes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailStatus(null);
    
    try {
      let response;
      let inviteId;
      
      if (editingInvite) {
        setEmailStatus({ type: 'sending', message: 'Updating invite...' });
        response = await updateInviteList(editingInvite.invite_id, formData);
        inviteId = editingInvite.invite_id;
      } else {
        setEmailStatus({ type: 'sending', message: 'Adding invite...' });
        response = await createInviteList(formData);
        // Extract invite ID from response or generate one
        inviteId = response.data?.invite_id || response.data?.id || `INV${String(safeInvites.length + 1).padStart(3, '0')}`;
      }

      if (response.success) {
        // Show success message
        if (editingInvite) {
          setEmailStatus({ type: 'success', message: 'Invite updated successfully!' });
        } else {
          setEmailStatus({ type: 'success', message: `Invite added successfully for ${formData.email || formData.firstname || 'the recipient'}!` });
        }
        setTimeout(() => setEmailStatus(null), 5000);
        
        // Send email after successful form submission
        if (formData.email && inviteId) { 
          try {
            console.log('📧 Sending email for invite:', inviteId);
            
            /**const emailResponse = await apiCall(SEND_MAIL, {
              method: 'POST',
              data: {
                session: inviteId,
                site_id: user?.site_id || formData.site_id || '',
                type: 'invite-list',
                email: formData.email,
                firstname: formData.firstname,
                access_code: formData.access_code
              }
            });
            
            console.log('📨 Email API response:', emailResponse);
            
            if (emailResponse.success) {
              setEmailStatus({ type: 'success', message: 'Email sent successfully!' });
              // Clear success message after 3 seconds
              setTimeout(() => setEmailStatus(null), 3000);
            } else {
              setEmailStatus({ type: 'error', message: 'Failed to send email: ' + (emailResponse.error || 'Unknown error') });
            }*/
          } catch (emailError) {
            console.error('Email sending error:', emailError);
            // Don't override the success message if invite was created/updated successfully
          }
        }
        
        setShowModal(false);
        setEditingInvite(null);
        resetForm();
        fetchInvites();
      } else {
        // Show error message if API call failed
        if (editingInvite) {
          setEmailStatus({ type: 'error', message: 'Failed to update invite: ' + (response.error || 'Unknown error') });
        } else {
          setEmailStatus({ type: 'error', message: 'Failed to add invite: ' + (response.error || 'Unknown error') });
        }
        setTimeout(() => setEmailStatus(null), 5000);
        
        // Fallback to mock data operations if API fails
        if (editingInvite) {
          setInvites(prev => prev.map(invite => 
            invite.invite_id === editingInvite.invite_id 
              ? { ...invite, ...formData, updated_at: new Date().toISOString() }
              : invite
          ));
        } else {
          const newInvite = {
            invite_id: `INV${String(safeInvites.length + 1).padStart(3, '0')}`,
            ...formData,
            create_at: new Date().toISOString()
          };
          setInvites(prev => [...(Array.isArray(prev) ? prev : []), newInvite]);
        }
        
        // Send email for mock data too
        if (formData.email) {
          try {
            setEmailStatus({ type: 'sending', message: 'Sending email...' });
            const mockInviteId = editingInvite ? editingInvite.invite_id : `INV${String(safeInvites.length + 1).padStart(3, '0')}`;
            console.log('📧 Sending email for mock invite:', mockInviteId);
            
            const emailResponse = await apiCall(SEND_MAIL, {
              method: 'POST',
              data: {
                //session: mockInviteId,
                site_id: user?.site_id || formData.site_id || '',
                type: 'invite-list',
                email: formData.email,
                firstname: formData.firstname,
                access_code: formData.access_code
              }
            });
            
            console.log('📨 Email API response:', emailResponse);
            
            if (emailResponse.success) {
              setEmailStatus({ type: 'success', message: 'Email sent successfully!' });
              // Clear success message after 3 seconds
              setTimeout(() => setEmailStatus(null), 3000);
            } else {
              setEmailStatus({ type: 'error', message: 'Failed to send email: ' + (emailResponse.error || 'Unknown error') });
            }
          } catch (emailError) {
            console.error('Email sending error:', emailError);
            setEmailStatus({ type: 'error', message: 'Failed to send email: ' + emailError.message });
          }
        }
        
        setShowModal(false);
        setEditingInvite(null);
        resetForm();
      }
    } catch (error) {
      // Show error message
      if (editingInvite) {
        setEmailStatus({ type: 'error', message: 'Failed to update invite: ' + error.message });
      } else {
        setEmailStatus({ type: 'error', message: 'Failed to add invite: ' + error.message });
      }
      setTimeout(() => setEmailStatus(null), 5000);
      
      // Fallback to mock data operations if API throws error
      if (editingInvite) {
        setInvites(prev => prev.map(invite => 
          invite.invite_id === editingInvite.invite_id 
            ? { ...invite, ...formData, updated_at: new Date().toISOString() }
            : invite
        ));
      } else {
        const newInvite = {
          invite_id: `INV${String(safeInvites.length + 1).padStart(3, '0')}`,
          ...formData,
          create_at: new Date().toISOString()
        };
        setInvites(prev => [...(Array.isArray(prev) ? prev : []), newInvite]);
      }
      
      // Send email for mock data too
      if (formData.email) {
        try {
          setEmailStatus({ type: 'sending', message: 'Sending email...' });
          const mockInviteId = editingInvite ? editingInvite.invite_id : `INV${String(safeInvites.length + 1).padStart(3, '0')}`;
          console.log('📧 Sending email for mock invite:', mockInviteId);
          
          const emailResponse = await apiCall(SEND_MAIL, {
            method: 'POST',
            data: {
              session: mockInviteId,
              site_id: user?.site_id || formData.site_id || '',
              type: 'invite-list',
              email: formData.email,
              firstname: formData.firstname,
              access_code: formData.access_code
            }
          });
          
          console.log('📨 Email API response:', emailResponse);
          
          if (emailResponse.success) {
            setEmailStatus({ type: 'success', message: 'Email sent successfully!' });
          } else {
            setEmailStatus({ type: 'error', message: 'Failed to send email: ' + (emailResponse.error || 'Unknown error') });
          }
        } catch (emailError) {
          console.error('Email sending error:', emailError);
          setEmailStatus({ type: 'error', message: 'Failed to send email: ' + emailError.message });
        }
      }
      
      setShowModal(false);
      setEditingInvite(null);
      resetForm();
    }
  };

  const handleEdit = (invite) => {
    setEditingInvite(invite);
    setFormData({
      invite_id: invite.invite_id || '',
      firstname: invite.firstname || '',
      email: invite.email || '',
      access_code: invite.access_code || '',
      create_at: invite.create_at || ''
    });
    setShowModal(true);
  };

  const handleDelete = (inviteId) => {
    const invite = safeInvites.find(inv => inv.invite_id === inviteId);
    setConfirmModal({
      type: 'delete',
      title: 'Delete Invite',
      message: `Are you sure you want to delete the invite for ${invite?.firstname || invite?.email || 'this invite'}? This action cannot be undone.`,
      onConfirm: async () => {
      try {
        setEmailStatus({ type: 'sending', message: 'Deleting invite...' });
        const response = await deleteInviteList(inviteId);
        if (response.success) {
          setEmailStatus({ type: 'success', message: 'Invite deleted successfully!' });
          setTimeout(() => setEmailStatus(null), 5000);
          fetchInvites();
        } else {
          setEmailStatus({ type: 'error', message: 'Failed to delete invite: ' + (response.error || 'Unknown error') });
          setTimeout(() => setEmailStatus(null), 5000);
          setInvites(prev => prev.filter(invite => invite.invite_id !== inviteId));
        }
      } catch (error) {
        setEmailStatus({ type: 'error', message: 'Failed to delete invite: ' + error.message });
        setTimeout(() => setEmailStatus(null), 5000);
        setInvites(prev => prev.filter(invite => invite.invite_id !== inviteId));
      }
        setConfirmModal(null);
      },
      onCancel: () => setConfirmModal(null)
    });
  };

  const handleResend = (invite) => {
    if (!invite.email) {
      alert('No email address found for this invite');
      return;
    }

    setConfirmModal({
      type: 'resend',
      title: 'Resend Email',
      message: `Are you sure you want to resend the email to ${invite.firstname || invite.email} (${invite.email})?`,
      onConfirm: async () => {
    try {
      setEmailStatus({ type: 'sending', message: 'Resending email...' });
      
      const emailResponse = await apiCall(PUBLIC_SEND_MAIL_BY_INVITE, {
        method: 'POST',
        data: {
          invite_id: invite.invite_id || invite.id || '',
          site_id: user?.site_id || invite.site_id || '',
          email: invite.email,
          firstname: invite.firstname || '',
          access_code: invite.access_code || '',
          type: 'invite-list'
        }
      });
      
      console.log('📨 Resend email API response:', emailResponse);
      
      if (emailResponse.success) {
        setEmailStatus({ type: 'success', message: `Email resent successfully to ${invite.email}!` });
        setTimeout(() => setEmailStatus(null), 5000);
      } else {
        setEmailStatus({ type: 'error', message: 'Failed to resend email: ' + (emailResponse.error || 'Unknown error') });
      }
    } catch (error) {
      console.error('Email resend error:', error);
      setEmailStatus({ type: 'error', message: 'Failed to resend email: ' + error.message });
    }
        setConfirmModal(null);
      },
      onCancel: () => setConfirmModal(null)
    });
  };

  const resetForm = () => {
    setFormData({
      invite_id: '',
      firstname: '',
      email: '',
      access_code: '',
      create_at: ''
    });
    setEmailStatus(null);
  };

  const openCreateModal = () => {
    setEditingInvite(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingInvite(null);
    resetForm();
  };


  const filteredInvites = searchTerm ? safeInvites.filter(invite => {
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Helper function to safely convert values to strings and check for matches
    const safeStringMatch = (value) => {
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(searchLower);
    };
    
    return (
      safeStringMatch(invite.site_id) ||
      safeStringMatch(invite.firstname) ||
      safeStringMatch(invite.email) ||
      safeStringMatch(invite.access_code) ||
      safeStringMatch(invite.event_status) ||
      safeStringMatch(invite.event_action)
    );
  }) : safeInvites;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Email Status Toast Notification */}
      {emailStatus && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg animate-in slide-in-from-top-5 ${
          emailStatus.event_type === 'success' ? 'bg-green-50 border-2 border-green-400' :
          emailStatus.event_type === 'error' ? 'bg-red-50 border-2 border-red-400' :
          emailStatus.event_type === 'sending' ? 'bg-blue-50 border-2 border-blue-400' :
          'bg-gray-50 border-2 border-gray-400'
        }`}>
          <div className="flex items-center">
            {emailStatus.event_type === 'success' && (
              <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
            )}
            {emailStatus.event_type === 'error' && (
              <XCircle className="h-6 w-6 text-red-600 mr-3" />
            )}
            {emailStatus.event_type === 'sending' && (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
            )}
            <span className={`text-base font-semibold ${
              emailStatus.event_type === 'success' ? 'text-green-900' :
              emailStatus.event_type === 'error' ? 'text-red-900' :
              emailStatus.event_type === 'sending' ? 'text-blue-900' :
              'text-gray-900'
            }`}>
              {emailStatus.message}
            </span>
          </div>
        </div>
      )}
  
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invite List</h1>
          <p className="text-gray-600">Manage survey and event invitations.</p>
        </div>
        <button 
          onClick={openCreateModal}
          className="btn-primary flex items-center space-x-2 mt-4 sm:mt-0"
        >
          <Plus className="h-5 w-5" />
          <span>Add Invite</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="card mt-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search invites by name, email, code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Invites Table */}
      <div className="card mt-5">
        {/* Table Header Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Invite List
                {searchTerm && (
                  <span className="ml-2 text-sm font-normal text-primary-600">
                    (filtered by: "{searchTerm}")
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600">
                {searchTerm ? (
                  <>
                    Showing {filteredInvites.length} of {safeInvites.length} invites
                    <span className="text-primary-600"> (filtered by search: "{searchTerm}")</span>
                  </>
                ) : (
                  <>
                    Showing {safeInvites.length} invites with {tableColumns.length} columns
                  </>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto table-container">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {tableColumns.map((column) => (
                  <th 
                    key={column.key} 
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.width}`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeInvites.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-2">
                      <User className="h-12 w-12 text-gray-300" />
                      <p className="text-lg font-medium">No invites found</p>
                      <p className="text-sm">Create your first invite to get started</p>
                      <button
                        onClick={openCreateModal}
                        className="btn-primary mt-2"
                      >
                        Add Invite
                      </button>
                    </div>
                  </td>
                </tr>
              ) : filteredInvites.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-2">
                      <Search className="h-12 w-12 text-gray-300" />
                      <p className="text-lg font-medium">No invites match your search</p>
                      <p className="text-sm">Try adjusting your search terms</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredInvites.map((invite) => (
                  <tr key={invite.invite_id} className="hover:bg-gray-50">
                    {tableColumns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {renderTableCell(column, invite)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between gap-4 sm:hidden">
            <button className="btn-secondary">Previous</button>
            <button className="btn-secondary">Next</button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredInvites.length}</span> of{' '}
                <span className="font-medium">{safeInvites.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm gap-2">
                <button className="btn-secondary rounded-md">Previous</button>
                <button className="btn-secondary rounded-md">Next</button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Invite Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingInvite ? 'Edit Invite' : 'Send Invite'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.firstname}
                      onChange={(e) => setFormData({...formData, firstname: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Enter first name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Access Code *
                  </label>
                  <select
                    value={formData.access_code}
                    onChange={(e) => setFormData({...formData, access_code: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    required
                    disabled={loadingAccessCodes}
                  >
                    <option value="">
                      {loadingAccessCodes ? 'Loading access codes...' : 'Select access code'}
                    </option>
                    {accessCodes.map((accessCode) => (
                      <option key={accessCode.access_code_id || accessCode.id} value={accessCode.access_code || accessCode.code}>
                        {accessCode.access_code || accessCode.code} 
                        {accessCode.description ? ` - ${accessCode.description}` : ''}
                        {accessCode.site_id ? '' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    {editingInvite ? 'Update' : 'Send'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center mb-4">
                {confirmModal.type === 'delete' ? (
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                ) : (
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                )}
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {confirmModal.title}
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  {confirmModal.message}
                </p>
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={confirmModal.onCancel}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmModal.onConfirm}
                    className={`px-4 py-2 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                      confirmModal.type === 'delete'
                        ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    }`}
                  >
                    {confirmModal.type === 'delete' ? 'Delete' : 'Resend'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteList;
